import { useEffect, useRef, useState } from 'react'
import { useIsFetching, useIsMutating } from '@tanstack/react-query'

import { ApiError } from '@/lib/api'
import { AGENT_PERMISSIONS, PERMISSION_KEYS } from '@/lib/permissions'
import { usePermissionsAt } from '@/lib/use-permissions'
import { policyCatalogKeys, policyOrderKeys, usePolicyOrder, PolicySelector, PRINTER_SELECTOR_FIELDS } from '@/modules/hierarchy'
import { Button } from '@/ui/button'
import { DialogDescription, DialogTitle } from '@/ui/dialog'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { TextField } from '@/ui/text-field'
import { DEFAULT_FORMAT_ORDER, DEFAULT_COUNTERS } from '../../lib/print-settings/print-settings'
import { preferredCounterPolicyApi, PREFERRED_COUNTER_POLICY_TYPE, type PreferredCounterPolicy, type PreferredCounterEdit } from '../../api/preferred-counter-policy-api'
import { comparePreferredCounterPolicy, resolvePreferredCounterPolicy, type PreferredCounterChoices, type PreferredCounterComparison } from '../../lib/printer-policy-comparison'
import { PrinterPolicyConflictReview, PrinterPolicyDraftCopy } from '../../lib/printer-policy-conflict-review'
import { PreferredCounterFields } from './preferred-counter-fields'
import { preferredCounterPolicyError, useSavePreferredCounterPolicy } from './queries'
import { usePrinterPolicyReconciliation } from '../printer-policies/use-printer-policy-reconciliation'

export function PreferredCounterPolicyForm({
  entityId,
  policy,
  onClose,
  onCreateLockChange,
  onPendingChange,
  reloading = false,
  reloadError = null,
}: {
  entityId: string
  policy: PreferredCounterPolicy | null
  onClose: () => void
  onCreateLockChange?: (locked: boolean) => void
  onPendingChange?: (pending: boolean) => void
  onReload?: () => void
  reloading?: boolean
  reloadError?: string | null
}) {
  const save = useSavePreferredCounterPolicy(entityId)
  const reconciliation = usePrinterPolicyReconciliation(entityId, PREFERRED_COUNTER_POLICY_TYPE)
  const { can } = usePermissionsAt(entityId)
  const canEdit = can(PERMISSION_KEYS.printerPolicyManage) && can(AGENT_PERMISSIONS.read)
  const currentCanEdit = useRef(canEdit)
  useEffect(() => { currentCanEdit.current = canEdit }, [canEdit])
  const nameInput = useRef<HTMLInputElement>(null)
  const [baseline, setBaseline] = useState(policy)
  const [latest, setLatest] = useState<PreferredCounterPolicy | null>(null)
  const [comparison, setComparison] = useState<PreferredCounterComparison | null>(null)
  const [choices, setChoices] = useState<PreferredCounterChoices>({})
  const [needsReview, setNeedsReview] = useState(false)
  const [reviewVisible, setReviewVisible] = useState(false)
  const [repeated, setRepeated] = useState(false)
  const [loadingLatest, setLoadingLatest] = useState(false)
  const [latestError, setLatestError] = useState<string | null>(null)
  const [failedBaseline, setFailedBaseline] = useState<PreferredCounterPolicy | null>(null)
  const [replacementVersion, setReplacementVersion] = useState(0)
  const order = usePolicyOrder(entityId, PREFERRED_COUNTER_POLICY_TYPE, policy === null)
  const saving = useIsMutating({ mutationKey: ['printer-policy-save', entityId, PREFERRED_COUNTER_POLICY_TYPE] })
  const deleting = useIsMutating({ mutationKey: ['printer-policy-delete', entityId, PREFERRED_COUNTER_POLICY_TYPE] })
  const moving = useIsMutating({ mutationKey: ['policy-move', entityId, PREFERRED_COUNTER_POLICY_TYPE] })
  const toggling = useIsMutating({ mutationKey: ['policy-enabled', entityId, PREFERRED_COUNTER_POLICY_TYPE] })
  const refreshingCatalog = useIsFetching({ queryKey: policyCatalogKeys.all(entityId) })
  const refreshingOrder = useIsFetching({ queryKey: policyOrderKeys.type(entityId, PREFERRED_COUNTER_POLICY_TYPE) })
  const [operationId] = useState(() => crypto.randomUUID())
  const [attemptedCreate, setAttemptedCreate] = useState<PreferredCounterEdit | null>(null)
  const [uncertainCreate, setUncertainCreate] = useState(false)
  const [name, setName] = useState(policy?.name ?? '')
  const [selectorExpression, setSelectorExpression] = useState(policy?.selectorExpression ?? '')
  const [selectorGui, setSelectorGui] = useState(policy?.selectorGui ?? null)
  const [selectorValid, setSelectorValid] = useState(true)
  const [value, setValue] = useState(policy?.value ?? {
    formatPriority: [...DEFAULT_FORMAT_ORDER],
    countersToDisplay: [...DEFAULT_COUNTERS],
  })
  const pending = save.isPending || reloading || loadingLatest
  useEffect(() => {
    onPendingChange?.(pending)
    return () => onPendingChange?.(false)
  }, [pending, onPendingChange])
  const writeBlocked = pending || saving > 0 || deleting > 0 || moving > 0 || toggling > 0
    || refreshingCatalog > 0 || refreshingOrder > 0
  const fieldsLocked = pending || uncertainCreate || !canEdit || reviewVisible
  useEffect(() => {
    if (policy === null) onCreateLockChange?.(fieldsLocked)
  }, [policy, fieldsLocked, onCreateLockChange])
  const countersValid = value.countersToDisplay.length > 0
  const orderReady = policy !== null || (order.data !== undefined && !order.isFetching && !order.isError)
  const title = policy ? `Edit: ${policy.name}` : 'Create policy'
  const submitLabel = uncertainCreate ? 'Retry same policy' : 'Save policy'
  let actionLabel = submitLabel
  if (save.isPending) actionLabel = 'Saving…'
  if (needsReview) actionLabel = 'Review changes'
  const knownSaved = latest ?? baseline
  const protectedFormats = knownSaved !== null && !standardFormats(knownSaved.value.formatPriority)
  const draftText = JSON.stringify({ name, value, selectorExpression, selectorGui }, null, 2)
  const resolved = resolvedEdit()

  useEffect(() => {
    if (!reviewVisible && (needsReview || repeated)) nameInput.current?.focus()
  }, [reviewVisible, needsReview, repeated])

  function readDraft(): PreferredCounterEdit {
    return {
      name: name.trim(),
      value: structuredClone(value),
      selectorExpression: selectorExpression.trim() ? selectorExpression : null,
      selectorGui: selectorExpression.trim() ? selectorGui : null,
    }
  }

  function validEdit(edit: PreferredCounterEdit) {
    const expression = edit.selectorExpression ?? ''
    const currentExpression = selectorExpression.trim() ? selectorExpression : ''
    return Boolean(edit.name.trim()) && edit.name.trim().length <= 200
      && edit.value.countersToDisplay.length > 0 && expression.length <= 4096
      && (expression !== currentExpression || selectorValid)
  }

  function keepProtectedFormats(edit: PreferredCounterEdit, saved: PreferredCounterPolicy) {
    if (standardFormats(saved.value.formatPriority)) return edit
    return { ...edit, value: { ...edit.value, formatPriority: [...saved.value.formatPriority] } }
  }

  function resolvedEdit() {
    if (!comparison) return null
    const selected: PreferredCounterChoices = protectedFormats ? { ...choices, formatPriority: 'latest' } : choices
    const edit = resolvePreferredCounterPolicy(comparison, selected)
    return edit ? keepProtectedFormats(edit, comparison.latest) : null
  }

  function applyEdit(edit: PreferredCounterEdit, saved: PreferredCounterPolicy) {
    setName(edit.name)
    setValue(structuredClone(edit.value))
    setSelectorExpression(edit.selectorExpression ?? '')
    setSelectorGui(edit.selectorGui ?? null)
    setSelectorValid((edit.selectorExpression ?? '') !== selectorExpression || selectorValid)
    setReplacementVersion((version) => version + 1)
    setBaseline(saved)
    setNeedsReview(false)
    setRepeated(true)
    setReviewVisible(false)
  }

  async function fetchLatest(opened: PreferredCounterPolicy, draft: PreferredCounterEdit) {
    setNeedsReview(true)
    setReviewVisible(false)
    setLatest(null)
    setComparison(null)
    setChoices({})
    setLatestError(null)
    setFailedBaseline(opened)
    if (!currentCanEdit.current) {
      setLatestError('You no longer have permission to read and manage this policy. Your edits are kept.')
      return
    }
    setLoadingLatest(true)
    try {
      const saved = await preferredCounterPolicyApi.get(entityId, opened.id)
      if (!currentCanEdit.current) {
        setLatestError('You no longer have permission to read and manage this policy. Your edits are kept.')
        return
      }
      setLatest(saved)
      if (saved.revision === opened.revision) {
        setNeedsReview(false)
        setLatestError('The request conflicts with an earlier operation, but the saved revision has not changed. Your edits are kept. Review your values before trying again.')
        return
      }
      if (saved.revision > opened.revision) {
        await reconciliation.reload()
        if (!currentCanEdit.current) {
          setLatestError('You no longer have permission to read and manage this policy. Your edits are kept.')
          return
        }
      }
      setBaseline(opened)
      setComparison(comparePreferredCounterPolicy(opened, draft, saved))
      setChoices({})
      setReviewVisible(true)
      save.reset()
    } catch (error) {
      setLatestError(preferredCounterPolicyError(error, 'load'))
    } finally {
      setLoadingLatest(false)
    }
  }

  function update(saved: PreferredCounterPolicy, candidate: PreferredCounterEdit) {
    const edit = keepProtectedFormats(candidate, saved)
    if (writeBlocked || !currentCanEdit.current || !validEdit(edit)) return
    setLatestError(null)
    save.mutate({ policy: saved, operationId, edit }, {
      onSuccess: onClose,
      onError: (error) => {
        if (error instanceof ApiError && error.code === 'PRINTER_POLICY_CONFLICT') void fetchLatest(saved, edit)
      },
    })
  }

  function reviewCurrentDraft() {
    if (writeBlocked || !baseline || !latest || !canEdit) return
    setComparison(comparePreferredCounterPolicy(baseline, readDraft(), latest))
    setChoices({})
    setReviewVisible(true)
  }

  function submit() {
    if (writeBlocked || !canEdit) return
    if (needsReview) {
      reviewCurrentDraft()
      return
    }
    if (!name.trim() || !orderReady || !selectorValid || !countersValid) return
    if (baseline) {
      update(baseline, readDraft())
      return
    }
    const edit = attemptedCreate ?? {
      name: name.trim(),
      value: { ...value },
      selectorExpression: selectorExpression.trim() ? selectorExpression : null,
      selectorGui: selectorExpression.trim() ? selectorGui : null,
      ...(policy === null && order.data ? { placement: {
        beforePolicyId: null,
        expectedOrder: order.data.map(({ id, revision }) => ({ id, revision })),
      } } : {}),
    }
    if (policy === null) setAttemptedCreate(edit)
    save.mutate(
      { policy, operationId, edit },
      {
        onSuccess: onClose,
        onError: handleCreateFailure,
      },
    )
  }

  function handleCreateFailure(error: unknown) {
    if (policy !== null) return
    const confirmsNoCommittedCreate = error instanceof ApiError && error.code === 'POLICY_ORDER_CONFLICT'
    const rejectionIsCertain = isDefiniteCreateRejection(error)
      && (!uncertainCreate || confirmsNoCommittedCreate)
    if (rejectionIsCertain) {
      setAttemptedCreate(null)
      setUncertainCreate(false)
      return
    }
    setUncertainCreate(true)
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div hidden={reviewVisible}>
        <form
          className="flex flex-col gap-6"
          aria-label={title}
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <section className="flex flex-col gap-4">
            {policy && (
              <div className="flex flex-col gap-1">
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>Choose which counters new printers display and the order used to select their values.</DialogDescription>
              </div>
            )}
            <TextField
              ref={nameInput}
              name="name"
              label="Policy name"
              kind="text"
              required
              maxLength={200}
              value={name}
              disabled={fieldsLocked}
              onChange={setName}
              onBlur={() => undefined}
            />
            <PreferredCounterFields entityId={entityId} value={value} onChange={setValue} disabled={fieldsLocked} formatsReadOnly={needsReview && protectedFormats} />
          </section>
          <PolicySelector
            targetFields={PRINTER_SELECTOR_FIELDS}
            targetLabel="printers"
            entityId={entityId}
            value={selectorExpression}
            gui={selectorGui}
            disabled={fieldsLocked}
            replacementVersion={replacementVersion}
            onChange={(expression, valid, gui) => {
              setSelectorExpression(expression)
              setSelectorValid(valid)
              setSelectorGui(gui)
            }}
          />
          {policy === null && (
            <>
              {order.isLoading && <LoadingNotice message="Loading policy order…" />}
              {order.isError && <RetryNotice message="Couldn’t load the policy order." disabled={writeBlocked}
                onRetry={() => { if (!writeBlocked) void order.refetch() }} />}
            </>
          )}
          {uncertainCreate && (
            <p role="status" className="text-muted-foreground text-xs">
              This request may have already succeeded. Retry it to confirm, or cancel and refresh
              the list before creating another policy.
            </p>
          )}
          {save.isError && !needsReview && (
            <p role="alert" className="text-destructive text-xs">
              {preferredCounterPolicyError(save.error)}
            </p>
          )}
          {save.isError && save.error instanceof ApiError && save.error.code === 'POLICY_ORDER_CONFLICT' && policy === null && (
            <Button type="button" variant="outline" disabled={writeBlocked} onClick={() => {
              if (writeBlocked) return
              void order.refetch().then((result) => {
                if (!result.isSuccess) return
                save.reset()
              })
            }}>Reload policy order</Button>
          )}
          {reloadError && (
            <p role="alert" className="text-destructive text-xs">
              {reloadError}
            </p>
          )}
          {loadingLatest && <LoadingNotice message="Loading the latest saved policy…" />}
          {latestError && <RetryNotice message={latestError} disabled={writeBlocked || !canEdit} onRetry={() => {
            if (writeBlocked) return
            if (failedBaseline) void fetchLatest(failedBaseline, readDraft())
          }} />}
          {needsReview && <>
            <p role="status" className="text-muted-foreground text-xs">Your edits are kept. Review the latest saved policy before saving.</p>
            <PrinterPolicyDraftCopy draftText={draftText} />
          </>}
          <div className="border-border flex justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" disabled={pending} onClick={onClose}>
              Cancel
            </Button>
            <Button type={needsReview ? 'button' : 'submit'} onClick={needsReview ? reviewCurrentDraft : undefined} disabled={writeBlocked || !canEdit || (!needsReview && (!name.trim() || !orderReady || !selectorValid || !countersValid)) || (needsReview && latest === null)}>
              {actionLabel}
            </Button>
          </div>
        </form>
      </div>
      {reviewVisible && comparison && <PrinterPolicyConflictReview
        kind="counter" comparison={comparison} choices={choices} formatsReadOnly={protectedFormats}
        onChoose={(field, choice) => setChoices({ ...choices, [field]: choice })}
        draftText={draftText} repeated={repeated} disabled={pending || !canEdit}
        resolveDisabled={writeBlocked}
        saveDisabled={resolved === null || !validEdit(resolved)}
        onContinue={() => setReviewVisible(false)}
        onEditMerged={() => { if (!writeBlocked && resolved && canEdit) applyEdit(resolved, comparison.latest) }}
        onSaveResolved={() => {
          if (writeBlocked || !resolved || !canEdit || !validEdit(resolved)) return
          applyEdit(resolved, comparison.latest)
          update(comparison.latest, resolved)
        }}
      />}
    </div>
  )
}

function standardFormats(formats: string[]) {
  return formats.length === DEFAULT_FORMAT_ORDER.length && DEFAULT_FORMAT_ORDER.every((format) => formats.includes(format))
}

function isDefiniteCreateRejection(error: unknown) {
  if (!(error instanceof ApiError)) return false
  if (error.code === 'PRINTER_POLICY_CONFLICT') return false
  return [400, 403, 404, 409].includes(error.status)
    || error.code === 'PRINTER_POLICY_BUSY'
    || error.code === 'PRINTER_POLICY_EVALUATOR_UNAVAILABLE'
}
